import React, { useState } from "react";
import {
  Bar,
  YAxis,
  XAxis,
  ResponsiveContainer,
  BarChart,
  ReferenceLine,
  ReferenceArea,
  Tooltip,
} from "recharts";
import PropTypes from "prop-types";
import ReactResizeDetector from "react-resize-detector";
import ReactTooltip from "react-tooltip";
import Annotation from "./Annotation";
import { titleCase } from "../../utils/dataHelpers/formatters";
import CandyBar from "./CandyBar";
import CustomTooltip from "./CustomTooltip";

const Chart = ({
  data,
  dataKey,
  stackCategories,
  yAxisWidth,
  xAxisLabel,
  refAreaRange,
  refAreaFill,
  refAreaLineColor,
  annotationLabel,
  dataYear,
  annotationDefaultPos,
  refAreaValue,
  tooltipPayload,
  disableDynamicPos,
  hideRefArea,
  hideRefLines,
}) => {
  const [chartDims, setChartDims] = useState({ width: 300, height: 100 });
  const [tooltipContent, setTooltipContent] = useState("");
  return (
    <React.Fragment>
      <ReactResizeDetector
        handleWidth
        handleHeight
        refreshMode="debounce"
        refreshRate={500}
        onResize={(width, height) => setChartDims({ width, height })}
      >
        {({ targetRef }) => (
          <ResponsiveContainer ref={targetRef}>
            <BarChart
              barCategoryGap={0}
              barGap={0}
              layout="vertical"
              width={500}
              height={800}
              data={data}
              margin={{
                top: 47,
                right: 13,
                left: 0,
                bottom: 0,
              }}
            >
              <pattern
                id="pattern-stripe"
                width="9"
                height="9"
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(45)"
              >
                <rect
                  width="2"
                  height="9"
                  transform="translate(0,0)"
                  fill="white"
                />
              </pattern>
              <mask id="mask-stripe">
                <rect
                  x="0"
                  y="0"
                  width="100%"
                  height="100%"
                  fill="url(#pattern-stripe)"
                />
              </mask>
              <XAxis
                type="number"
                tickLine={false}
                axisLine={false}
                height={20}
                label={xAxisLabel}
                domain={["dataMin", "dataMax"]}
                tickMargin={0}
                interval={2}
              />
              <YAxis
                width={yAxisWidth}
                interval={0}
                type="category"
                tickLine={false}
                axisLine={false}
                dataKey={dataKey}
              />
              {stackCategories.map(({ accessor, label, fill }) => {
                return (
                  <Bar
                    onMouseMove={({ payload }) => {
                      setTooltipContent(payload[accessor]);
                    }}
                    onMouseLeave={() => {
                      setTooltipContent("");
                    }}
                    key={accessor}
                    dataKey={accessor}
                    stackId="a"
                    fill={fill}
                    name={titleCase(label)}
                  />
                );
              })}
              <Tooltip
                content={<CustomTooltip tooltipContent={tooltipContent} />}
                wrapperStyle={{ top: 5 }}
              />
              {refAreaRange && (
                <Annotation
                  chartWidth={chartDims.width}
                  xRight={
                    refAreaRange[0] + (refAreaRange[1] - refAreaRange[0]) / 10
                  }
                  xLeft={
                    refAreaRange[0] +
                    ((refAreaRange[1] - refAreaRange[0]) / 10) * 9
                  }
                  y={dataYear}
                  textLabels={annotationLabel}
                  arrowLineWidth={8}
                  bottomPadding={11}
                  defaultPos={annotationDefaultPos}
                  disableDynamicPos={disableDynamicPos}
                  arrowLineHeight={0.8}
                  includeInfoIcon
                  tooltipPayload={tooltipPayload}
                />
              )}
              {refAreaRange && !hideRefArea && refAreaFill && (
                <ReferenceArea
                  x1={refAreaRange[0]}
                  x2={refAreaRange[1]}
                  isFront
                  fill={refAreaFill}
                  fillOpacity={1}
                  strokeWidth={0}
                  stroke="red"
                  ifOverflow="extendDomain"
                  shape={
                    <CandyBar
                      setTooltipContent={setTooltipContent}
                      value={refAreaValue}
                    />
                  }
                />
              )}
              {refAreaRange && !hideRefLines && (
                <ReferenceLine
                  x={refAreaRange[0]}
                  isFront
                  strokeWidth={1}
                  stroke={refAreaLineColor}
                  strokeDasharray="3 3"
                />
              )}
              {refAreaRange && !hideRefLines && (
                <ReferenceLine
                  x={refAreaRange[1]}
                  isFront
                  strokeWidth={1}
                  stroke={refAreaLineColor}
                  strokeDasharray="3 3"
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        )}
      </ReactResizeDetector>
      <ReactTooltip type="dark" />
    </React.Fragment>
  );
};
//

Chart.defaultProps = {
  yAxisWidth: 50,
  xAxisLabel: null,
  refAreaRange: null,
  refAreaFill: null,
  refAreaLineColor: "white",
  annotationLabel: null,
  annotationDefaultPos: undefined,
  refAreaValue: null,
  tooltipPayload: undefined,
  disableDynamicPos: undefined,
  hideRefArea: false,
  hideRefLines: false,
};

Chart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  dataKey: PropTypes.string.isRequired,
  stackCategories: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any))
    .isRequired,
  yAxisWidth: PropTypes.number,
  xAxisLabel: PropTypes.string,
  refAreaValue: PropTypes.number,
  refAreaRange: PropTypes.arrayOf(PropTypes.number),
  refAreaFill: PropTypes.string,
  refAreaLineColor: PropTypes.string,
  annotationLabel: PropTypes.arrayOf(PropTypes.any),
  dataYear: PropTypes.number.isRequired,
  annotationDefaultPos: PropTypes.string,
  tooltipPayload: PropTypes.node,
  disableDynamicPos: PropTypes.bool,
  hideRefArea: PropTypes.bool,
  hideRefLines: PropTypes.bool,
};

export default Chart;
